require('./brokerRightsCashExchangeSidebar.scss');

// @ vendor
const React = require('react');
const PropTypes = require('prop-types');
const moment = require('moment');

// @Helpers
const { FormattedText, formatText, getLanguage } = require('core/i18n').i18n;
const { formatAmount, formatAmountObject, formatCurrency, getPlainAmountObject } = require('utilities/currencyHelper');
const { formatAccountTextWithSpaces } = require('utilities/ibanHelper');
const DateHelper = require('utilities/dateHelper');

// @Commons
const { OneLineSymbolAmount } = require('commonsComponents/oneLineSymbolAmount');
const { LoadingSection } = require('commonsComponents/loadingSection');

// @Components
const { OKSectionBox } = require('commonsComponents/OKSectionBox');

// @Constants
const { DATE_PICKER_CUSTOM_DATE_FORMAT } = require('constants/commonsComponents');
const {
    BROKER_TYPE_RIGHT_NEW_DECIMALS,
    DATE_FORMAT_SLASHES_SHORT_DAY_MONTH_YEAR,
    CHARGE_ACCOUNT_PREFIX,
    BROKER_CORPORATE_OPERATIONS_CASH_EXCHANGE_PRODUCT_TYPE,
    HYPHEN
} = require('constants/index');

class BrokerRightsCashExchangeSidebar extends React.Component {
    buildHeader() {
        const { immSelectedOperation } = this.props;
        return (
            <div className="broker-rights-cash-exchange-sidebar__head-item">
                <h4>
                    <div className="broker-rights-cash-exchange-sidebar__head-item-label">
                        <FormattedText value="brokerRightsCashExchange-brokerRightsCashExchange_sidebarTitle" format="uppercase"/>
                    </div>
                </h4>
                <div className="broker-rights-cash-exchange-sidebar__head-item-name">
                    {immSelectedOperation.get('descriptionValue')}
                </div>
            </div>
        );
    }

    buildQuotationSection() {
        const { immProductFile } = this.props;
        const quotationAmount = immProductFile.getIn(['productValue', 'amount']);
        const quotationCurrency = immProductFile.getIn(['productValue', 'currency']);
        const quotationDate = immProductFile.get('updatePriceDate');
        const quotationTime = immProductFile.get('updatePriceTime');
        let formattedQuotationAmount;
        let formattedQuotationCurrency;
        let formattedQuotationDateTime;
        if(quotationAmount && quotationDate) {
            const dateObject = DateHelper.buildFormattedDate(moment(quotationDate, DATE_FORMAT_SLASHES_SHORT_DAY_MONTH_YEAR), DATE_PICKER_CUSTOM_DATE_FORMAT);
            formattedQuotationAmount = formatAmount(quotationAmount, getLanguage(), BROKER_TYPE_RIGHT_NEW_DECIMALS);
            formattedQuotationCurrency = formatCurrency(quotationCurrency);
            formattedQuotationDateTime = `${dateObject} ${HYPHEN} ${quotationTime}`;
        } else {
            formattedQuotationAmount = HYPHEN;
        }

        return (
            <div className="broker-rights-cash-exchange-sidebar__item">
                <h4>
                    <div className="broker-rights-cash-exchange-sidebar__item-label">
                        <FormattedText
                            value="brokerRightsCashExchange-brokerRightsCashExchange_valueLabel"
                            format="uppercase"
                        />
                    </div>
                </h4>
                <div className="broker-rights-cash-exchange-sidebar__item-name">
                    {formattedQuotationAmount} {formattedQuotationCurrency}
                </div>
                <div className="broker-rights-cash-exchange-sidebar__item-additional-information">
                    {formattedQuotationDateTime}
                </div>
            </div>
        );
    }

    buildContractSection() {
        const { immSelectedContract } = this.props;
        const contractAlias = immSelectedContract.get('walletAccountType');
        const valueContractNumber = immSelectedContract.getIn(['cuenta', 'fullNumber']);
        const controlDigit = immSelectedContract.getIn(['cuenta', 'digitodecontrol']);
        const fullValueContractNumber = `${CHARGE_ACCOUNT_PREFIX}${controlDigit}${valueContractNumber}`;
        return (
            <div className="broker-rights-cash-exchange-sidebar__item">
                <h4>
                    <div className="broker-rights-cash-exchange-sidebar__item-label">
                        <FormattedText
                            value="brokerRightsCashExchange-brokerRightsCashExchange_contractLabel"
                            format="uppercase"
                        />
                    </div>
                </h4>
                <div className="broker-rights-cash-exchange-sidebar__item-name">
                    {contractAlias}
                </div>
                <div className="broker-rights-cash-exchange-sidebar__item-additional-information">
                    <FormattedText
                        value="brokerRightsCashExchange-brokerRightsCashExchange_contractNumber"
                        injectedStrings={[fullValueContractNumber]}
                    />
                </div>
            </div>
        );
    }

    buildHolderSection() {
        const { immSelectedContract } = this.props;
        const holder = immSelectedContract.get('owner');
        const intervention = formatText(`interventionTypes-${immSelectedContract.get('intervention').trim()}`);
        return (
            <div className="broker-rights-cash-exchange-sidebar__item">
                <h4>
                    <div className="broker-rights-buy-sidebar__item-label">
                        <FormattedText
                            value="brokerRightsCashExchange-brokerRightsCashExchange_holder"
                            format="uppercase"
                        />
                    </div>
                </h4>
                <div className="broker-rights-cash-exchange-sidebar__item-name">
                    {holder}
                </div>
                <div className="broker-rights-cash-exchange-sidebar__item-additional-information">
                    <FormattedText
                        value="brokerRightsCashExchange-brokerRightsCashExchange_interventionContainer"
                        injectedStrings={[intervention]}
                    />
                </div>
            </div>
        );
    }

    buildAccountSection() {
        const { immSelectedContract } = this.props;
        const accountNumber = immSelectedContract.getIn(['associatedAccount', 'number']);
        const formattedAccountNumber = formatAccountTextWithSpaces(accountNumber, [4, 4, 2]);
        const accountBalance = immSelectedContract.getIn(['associatedAccount', 'balance']);
        const plainAmountObject = getPlainAmountObject(accountBalance);
        return (
            <div className="broker-rights-cash-exchange-sidebar__item">
                <h4>
                    <div className="broker-rights-cash-exchange-sidebar__item-label">
                        <FormattedText
                            value="brokerRightsCashExchange-brokerRightsCashExchange_associatedAccount"
                            format="uppercase"
                            />
                    </div>
                </h4>
                <div className="broker-rights-cash-exchange-sidebar__item-name">
                    {formattedAccountNumber}
                </div>
                <div className="broker-rights-cash-exchange-sidebar__item-additional-information">
                    <FormattedText value="brokerRightsCashExchange-brokerRightsCashExchange_balancePrefix" />
                    <OneLineSymbolAmount
                        amountObject={plainAmountObject}
                        language={getLanguage()}
                        amountObjectFormatted={formatAmountObject(plainAmountObject, true)}
                    />
                    <FormattedText value="brokerRightsCashExchange-brokerRightsCashExchange_balanceSuffix" />
                </div>
            </div>
        );
    }

    buildDateSection() {
        const { immSelectedOperation } = this.props;
        const dateFormat = formatText(DATE_PICKER_CUSTOM_DATE_FORMAT);
        const date = DateHelper.buildFormattedDate(immSelectedOperation.get('closeDate'), dateFormat);
        return (
            <div className="broker-rights-cash-exchange-sidebar__item">
                <h4>
                    <div className="broker-rights-cash-exchange-sidebar__item-label">
                        <FormattedText value="brokerRightsCashExchange-brokerRightsCashExchange_date" format="uppercase" />
                    </div>
                </h4>
                <div className="broker-rights-cash-exchange-sidebar__item-name">
                    {date}
                </div>
            </div>
        );
    }

    buildProportionSection() {
        const { immSelectedOperation } = this.props;
        const proportion = immSelectedOperation.get('proportion');
        return (
            <div className="broker-rights-cash-exchange-sidebar__item">
                <h4>
                    <div className="broker-rights-cash-exchange-sidebar__item-label">
                        <FormattedText value="brokerRightsCashExchange-brokerRightsCashExchange_proportion" format="uppercase" />
                    </div>
                </h4>
                <div className="broker-rights-cash-exchange-sidebar__item-name">
                    {proportion}
                </div>
            </div>
        );
    }

    buildTitlesSection(){
        const { titles } = this.props;
        return (
            <div className="broker-rights-cash-exchange-sidebar__sidecolumn-extra-info">
                <h4>
                    <div className="broker-rights-cash-exchange-sidebar__item-label">
                        <FormattedText value="brokerRightsCashExchange-brokerRightsCashExchange_step1AmountText" format="uppercase" />
                    </div>
                </h4>
                <div className="broker-rights-cash-exchange-sidebar__item-name">
                    {titles}
                </div>
            </div>
        )
    }

    buildOperationTypeSection(){
        return (
            <div className="broker-rights-cash-exchange-sidebar__sidecolumn-extra-info">
                <h4>
                    <div className="broker-rights-cash-exchange-sidebar__item-label">
                        <FormattedText value="broker-contractActivityOperation" format="uppercase" />
                    </div>
                </h4>
                <div className="broker-rights-cash-exchange-sidebar__item-name">
                    {BROKER_CORPORATE_OPERATIONS_CASH_EXCHANGE_PRODUCT_TYPE}
                </div>
            </div>
        )
    }

    buildContent() {
        const { immProductFile, immSelectedContract, immSelectedOperation } = this.props;
        const productFileIsLoading = immProductFile.get('isFetching');
        let content;
        if(productFileIsLoading) {
            content = (
                <div className="broker-rights-cash-exchange-sidebar">
                    <LoadingSection />
                </div>
            );
        } else if(immSelectedContract && immSelectedOperation) {
            content = (
                <div className="broker-rights-cash-exchange-sidebar">
                    <OKSectionBox>
                        {this.buildOperationTypeSection()}
                        {this.buildHeader()}
                        {this.buildQuotationSection()}
                        {this.buildContractSection()}
                        {this.buildHolderSection()}
                        {this.buildAccountSection()}
                        {this.buildDateSection()}
                        {this.buildProportionSection()}
                        {this.buildTitlesSection()}
                    </OKSectionBox>
                    <OKSectionBox className="broker-rights-cash-exchange-sidebar__sidecolumn-extra-info">
                        <h4><FormattedText value="brokerSubscription-extraInfoTitle" format="uppercase" /></h4>
                        <p><FormattedText value="brokerRightsCashExchange-brokerRightsCashExchange_informativeMessageStep2" /></p>
                    </OKSectionBox>
                </div>
            );
        }
        return content;
    }

    render() {

        return (
            <OKSectionBox>
                {this.buildContent()}
            </OKSectionBox>
        );
    }
}

BrokerRightsCashExchangeSidebar.propTypes = {
    immProductFile: PropTypes.object.isRequired,
    titles: PropTypes.number,
    immSelectedContract: PropTypes.object,
    immSelectedOperation: PropTypes.object
}

module.exports = BrokerRightsCashExchangeSidebar;
